import { _getLogger } from '@doxyme/analytics';
import {
  BasicObservable,
  type Specs,
  type Mutable,
} from '@doxyme/extensions-common';
import type Instance from '../../instance';
import ExtensionCapability from '../../capability';
import { IframeRuntime } from '../../runtimes/iframeRuntime';

const logger = _getLogger('applet', {
  component: 'extensions',
  lib: 'bridge',
});

type AppletPayload = Specs.Applet.AppletPayload;
type AppletConfig = Specs.Applet.Config;
type Applet = Specs.Applet.Applet;
type AppletConstraints = Specs.Applet.Constraints;

function mkRuntime(
  instance: Instance,
  config: AppletConfig,
  hasDynamicHeight = false,
): IframeRuntime {
  const runtime = new IframeRuntime(instance, config, hasDynamicHeight);
  instance.addRuntime(runtime);
  return runtime;
}

export default class AppletCapability<
  Constraints extends AppletConstraints = AppletConstraints,
> extends ExtensionCapability<Constraints> {
  readonly _frames = new BasicObservable<Applet[]>([]);

  protected readonly hasDynamicHeight: boolean = false;

  protected readonly runtimes: Record<string, IframeRuntime> = {};

  /**
   * @hidden
   */
  override clear(): void {
    this.detachAllRuntimes();
    (this.runtimes as Mutable<typeof this.runtimes>) = {};
    super.clear();
  }

  /**
   * @hidden
   */
  override addInstance(instance: Instance): void {
    super.addInstance(instance);

    const showers = this.configsBy(instance).filter((c) => c.attach);

    this.attachRuntimes(instance, showers);
  }

  /**
   * @hidden
   */
  override removeInstance(instance: Instance): void {
    this.detachRuntimes(instance, this.configsBy(instance));
    super.removeInstance(instance);
  }

  protected attachRuntimes(instance: Instance, configs: AppletConfig[]): void {
    const frames = [];

    for (const config of configs) {
      const runtimeId = IframeRuntime.runtimeId(instance, config);
      let runtime = this.runtimes[runtimeId];

      if (!runtime) {
        runtime = mkRuntime(instance, config, this.hasDynamicHeight);
        this.runtimes[runtime.id] = runtime;
        frames.push(this.getFrameItem(instance, runtime, config));
      }
    }

    this._frames.value = [...this._frames.value, ...frames];
  }

  private detachAllRuntimes(): void {
    for (const instance of Object.values(this.instances)) {
      const configs = this.configsBy(instance);
      this.detachRuntimes(instance, configs);
    }
  }

  private detachRuntimes(instance: Instance, configs: AppletConfig[]): void {
    const runtimes: IframeRuntime[] = [];

    for (const config of configs) {
      const runtimeId = IframeRuntime.runtimeId(instance, config);
      const runtime = this.runtimes[runtimeId];

      if (runtime) {
        runtimes.push(runtime);
      }
    }

    // Unpublish frames from host app before removing runtimes backing them
    // We do this in one go to minimize triggers of onChange
    const runtimeIds = new Set(runtimes.map((r) => r.id));
    this._frames.value = this._frames.value.filter(
      (f) => !runtimeIds.has(f.runtimeId),
    );

    // Remove and destroy runtimes only after updating frames
    for (const r of runtimes) {
      delete this.runtimes[r.id];
      instance.removeRuntime(r.id);
      r.destroy();
    }
  }

  protected getRuntimeBy(runtimeId: string): IframeRuntime | undefined {
    return this.runtimes[runtimeId];
  }

  protected attach(
    configName: AppletPayload | undefined,
    instance: Instance,
  ): void {
    const configs = this.getConfigsForAttaching(configName, instance);
    this.attachRuntimes(instance, configs);
  }

  protected attachOne(
    configName: string | undefined,
    instance: Instance,
  ): void {
    const configs = this.getConfigsForAttaching(configName, instance);
    this.attachRuntimes(instance, configs.splice(0, 1)); // if multiple, only attach first config
  }

  protected getConfigsForAttaching(
    configName: AppletPayload | undefined,
    instance: Instance,
  ): AppletConfig[] {
    const configs = this.getConfigs(configName, instance);

    if (configs.length === 0) {
      logger.error('Applet configs were not found for attaching', {
        ...this.getLoggerProps(instance),
        configName,
      });
    }

    return configs;
  }

  protected detach(
    configName: AppletPayload | undefined,
    instance: Instance,
  ): void {
    const configs = this.getConfigs(configName, instance);
    this.detachRuntimes(instance, configs);
  }

  protected getConfigs(
    configName: AppletPayload | undefined,
    instance: Instance,
  ): AppletConfig[] {
    return configName !== undefined
      ? this.configsBy(instance).filter(({ name }) => name === configName)
      : this.configsBy(instance);
  }

  protected getFrameItem(
    instance: Instance,
    runtime: IframeRuntime,
    config: AppletConfig,
  ): Applet {
    return {
      frame: runtime.frame,
      runtimeId: runtime.id,
      permissions: config.permissions ?? [],
      contentHeight: runtime.contentHeight,
    };
  }

  protected getLoggerProps(instance: Instance): Record<string, unknown> {
    return {
      manifest: {
        id: instance.manifest.id,
        name: instance.manifest.name,
      },
    };
  }
}
